import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import { Wrench, Clock, Shield, HeadphonesIcon } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import { Cloud, Database, Shield as LucideShield } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function MaintenanceSupport() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Preventive Maintenance",
      description: "Regular system updates, security patches, and performance optimization to prevent issues.",
      icon: <Wrench className="h-8 w-8" />
    },
    {
      title: "24/7 Monitoring",
      description: "Round-the-clock system monitoring with proactive issue detection and resolution.",
      icon: <Clock className="h-8 w-8" />
    },
    {
      title: "SLA Agreements",
      description: "Service level agreements ensuring guaranteed response times and system uptime.",
      icon: <Shield className="h-8 w-8" />
    },
    {
      title: "Technical Support",
      description: "Expert technical support for troubleshooting and resolving system issues quickly.",
      icon: <HeadphonesIcon className="h-8 w-8" />
    }
  ];

  const supportPlans = [
    {
      name: "Basic Support",
      price: "$99",
      period: "per month",
      features: ["Business Hours Support", "Email Support", "Basic Monitoring", "Monthly Reports"],
      responseTime: "8 hours"
    },
    {
      name: "Premium Support",
      price: "$199",
      period: "per month",
      features: ["24/7 Support", "Phone & Email Support", "Advanced Monitoring", "Weekly Reports", "Priority Queue"],
      responseTime: "2 hours",
      popular: true
    },
    {
      name: "Enterprise Support",
      price: "$399",
      period: "per month",
      features: ["Dedicated Support Manager", "24/7 Phone Support", "Proactive Monitoring", "Real-time Reports", "Custom SLA"],
      responseTime: "30 minutes"
    }
  ];

  const maintenanceAreas = [
    { name: "Website Maintenance", icon: "fas fa-globe", description: "Regular updates and security patches" },
    { name: "Server Management", icon: "fas fa-server", description: "Server monitoring and optimization" },
    { name: "Database Optimization", icon: "fas fa-database", description: "Database performance tuning" },
    { name: "Security Updates", icon: "fas fa-shield-alt", description: "Regular security patches and updates" },
    { name: "Backup Management", icon: "fas fa-save", description: "Automated backups and recovery" },
    { name: "Performance Monitoring", icon: "fas fa-chart-line", description: "System performance analysis" }
  ];

  return (
    <>
      <SEO 
        title="IT Maintenance & Support UAE | ZeeXSoft"
        description="Reliable ongoing IT maintenance and technical support in Dubai & Sharjah. Proactive monitoring, updates, and 24/7 assistance."
        keywords="it maintenance uae, tech support dubai, managed services sharjah"
      />

      <main>
        

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 to-purple-50 py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto text-center relative z-10"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Hassle-free <span className="text-blue-600">IT Support</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
              Proactive maintenance and reliable technical support for your business
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                onClick={openDrawer}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
              >
                Get Started
              </Button>
              <Link href="/contact">
                <Button 
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Maintenance Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive IT maintenance solutions tailored to your needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mx-auto mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Plans */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Support Plans</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Choose the support level that matches your business needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {supportPlans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`bg-gray-50 p-8 rounded-xl relative ${plan.popular ? 'ring-2 ring-blue-600' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-blue-600">{plan.price}</span>
                      <span className="text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <div className="text-sm text-gray-600">Response: {plan.responseTime}</div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 text-xs">✓</span>
                        </div>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={openDrawer} className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'}`}>
                    Choose Plan
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Maintenance Areas */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Maintenance Areas</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive maintenance coverage for all your technology needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {maintenanceAreas.map((area, index) => (
                <motion.div
                  key={area.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-50 p-6 rounded-xl text-center"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${area.icon} text-xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{area.name}</h3>
                  <p className="text-gray-600 text-sm">{area.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SLA Benefits */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">SLA Benefits</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Service level agreements that guarantee performance and reliability
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Guaranteed Uptime",
                  description: "99.9% uptime guarantee with compensation for any downtime",
                  icon: "fas fa-clock"
                },
                {
                  title: "Response Time SLA",
                  description: "Guaranteed response times based on your support plan level",
                  icon: "fas fa-stopwatch"
                },
                {
                  title: "Performance Metrics",
                  description: "Detailed reporting on system performance and support metrics",
                  icon: "fas fa-chart-bar"
                }
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${benefit.icon} text-xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Process */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Support Process</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Streamlined support process for quick issue resolution
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Issue Report", description: "Report issue through multiple channels" },
                { step: "02", title: "Immediate Response", description: "Acknowledge and assess the issue" },
                { step: "03", title: "Resolution", description: "Implement solution and test thoroughly" },
                { step: "04", title: "Follow-up", description: "Ensure satisfaction and prevent recurrence" }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-bold text-blue-600">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Maintenance & Support" 
              services={[
                {
                  title: "Cloud Services",
                  description: "Managed cloud solutions with ongoing maintenance and support.",
                  icon: <Cloud className="h-8 w-8" />
                },
                {
                  title: "Cyber Security",
                  description: "Comprehensive security monitoring and protection services.",
                  icon: <LucideShield className="h-8 w-8" />
                },
                {
                  title: "Web Hosting",
                  description: "Reliable hosting services with maintenance included.",
                  icon: <Database className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Need Reliable Support?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Ensure your systems run smoothly with our comprehensive maintenance and support services
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openDrawer} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Get Support Plan
                </Button>
                <Link href="/contact">
                  <Button className="bg-gray-300 hover:bg-gray-400 text-gray-700">
                    Contact Support Team
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section for SEO */}
        <section className="faq-section bg-white py-12">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold">What is included in your maintenance and support plans?</h3>
                <p>Our plans include regular updates, security patches, 24/7 monitoring, technical support, and proactive issue resolution to keep your systems running smoothly.</p>
              </div>
              <div>
                <h3 className="font-semibold">Do you offer emergency support?</h3>
                <p>Yes, we provide emergency support with guaranteed response times as outlined in your SLA.</p>
              </div>
              <div>
                <h3 className="font-semibold">Can I upgrade my support plan later?</h3>
                <p>Absolutely! You can upgrade or customize your support plan at any time to match your growing business needs.</p>
              </div>
              <div>
                <h3 className="font-semibold">Do you maintain third-party websites or apps?</h3>
                <p>Yes, we offer maintenance and support for both ZeeXSoft-built and third-party systems.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links for SEO */}
        <section className="internal-links-section bg-gray-50 py-8">
          <div className="container mx-auto px-6 text-center">
            <span className="font-semibold">Explore more:</span>
            <div className="flex flex-wrap gap-4 justify-center mt-2">
              <Link href="/services" className="underline text-blue-700">All Services</Link>
              <Link href="/cloud-services" className="underline text-blue-700">Cloud Services</Link>
              <Link href="/contact" className="underline text-blue-700">Contact Us</Link>
              <Link href="/portfolio" className="underline text-blue-700">Portfolio</Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
